import React, { useMemo, useEffect, forwardRef, useImperativeHandle } from 'react';
import { FormField, FormService } from '@shared/services/FormServices';
import { FIELD_CLASSES } from '@shared/utils/constants';
import { DynamicForm } from '@shared/components/DynamicForm';
import { PageHeader } from '@shared/components/PageHeader';

export interface RateComponentRef {
  submit: () => Promise<any | null>;
  isDirty?: () => boolean;
}

interface FlatRateProps {
  data: any;
  hideHeader?: boolean;
  unit?: string;
}

export const FlatRate = forwardRef<RateComponentRef, FlatRateProps>(({ data, hideHeader, unit }, ref) => {
  const fields: FormField[] = useMemo(
    () => [
      {
        name: "rate",
        label: "Rate Amount",
        type: "decimal",
        placeholder: "0.00",
        required: true,
        helperText: "The flat rate amount to be charged.",
        customValidation: (val: any) => {
          if (val !== undefined && val !== null && (Number(val) < 0 || String(val).startsWith('-'))) {
            return "Negative rates are not allowed";
          }
          return true;
        },
        classes: FIELD_CLASSES,
      },
      {
        name: "unit",
        label: "Unit",
        type: "text",
        placeholder: "e.g. per gallon, per kWh",
        required: true,
        helperText: "The billing unit for this rate.",
        classes: FIELD_CLASSES,
        disabled: true,
        defaultValue: unit,
      }
    ],
    [unit]
  );

  const form = FormService.createForm({
    fields,
    mode: "onChange",
    validateOnMount: false,
    defaultValues: { ...data, unit: data?.unit || unit || "" },
  });

  useEffect(() => {
    if (data && Object.keys(data).length > 0) {
      form.reset({ ...data, unit: data.unit || unit || "" });
    }
  }, [data, form]);

  useEffect(() => {
    if (unit !== undefined) {
      if (form.getValues("unit") !== unit) {
        form.setValue("unit", unit, { shouldDirty: true });
      }
    }
  }, [unit, form]);



  useImperativeHandle(ref, () => ({
    submit: async () => {
      const isValid = await form.trigger();
      if (isValid) {
        return form.getValues();
      }
      return null;
    },
    isDirty: () => Object.keys(form.formState.dirtyFields).length > 0
  }));

  return (
    <div className="space-y-4">
     {!hideHeader && <PageHeader title={'Flat Rate'} subtitle='A single rate per unit applied uniformly to all consumption.'></PageHeader>}
      <DynamicForm
        fields={fields}
        form={form}
        config={{
          showSubmitButton: false,
          showResetButton: false,
        }}
        gridColumns={2}
        responsiveColumns={{ sm: 1, md: 2 }}
      />
    </div>
  );
});

FlatRate.displayName = "FlatRate";
